import React, { useMemo } from 'react';
import { IconButton, Stack } from '@mui/material';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import { DataTable } from '../../../../../components/ui';
import { getFieldRoleLabel, getUiFieldTypeLabel } from '../../utils/fieldTypeAdapter';
import { isFieldDeletable, isSubStatusField } from '../../utils/formStudioFormUtils';
import { shouldShowFieldOptionsPreview } from '../../utils/fieldOptionsUtils';
import FieldOptionsPreview from './FieldOptionsPreview';

export default function FormFieldsTableView({
  fields,
  allFields = [],
  fieldErrors,
  enableReorder = false,
  onEdit,
  onDelete,
  onReorder,
}) {
  const columns = useMemo(
    () => [
      {
        accessorKey: 'order_id',
        header: '#',
        size: 60,
      },
      {
        accessorKey: 'key_name',
        header: 'Field name',
        Cell: ({ row }) =>
          row.original.key_name === 'lead_id' ? 'record_id' : row.original.key_name,
      },
      {
        accessorKey: 'alias_name',
        header: 'Alias',
      },
      {
        accessorKey: 'key_type',
        header: 'Type',
        size: 110,
        Cell: ({ row }) => getUiFieldTypeLabel(row.original),
      },
      {
        accessorKey: 'role',
        header: 'Role',
        Cell: ({ row }) => getFieldRoleLabel(row.original),
      },
      {
        accessorKey: 'status_parent_key',
        header: 'Parent',
        Cell: ({ row }) => {
          if (!isSubStatusField(row.original)) return '—';
          const parent = allFields.find(
            (field) => field.key_name === row.original.status_parent_key
          );
          return parent?.alias_name || row.original.status_parent_key || '—';
        },
      },
      {
        accessorKey: 'key_options',
        header: 'Options',
        Cell: ({ row }) => {
          if (!shouldShowFieldOptionsPreview(row.original)) {
            return '—';
          }
          if (!row.original.key_options?.length) {
            return '—';
          }
          return (
            <FieldOptionsPreview
              keyOptions={row.original.key_options}
              optionColors={row.original.option_colors}
              maxVisible={4}
              variant="compact"
            />
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" onClick={() => onEdit(row.original)}>
              <EditOutlined fontSize="small" />
            </IconButton>
            {isFieldDeletable(row.original) ? (
              <IconButton size="small" onClick={() => onDelete(row.original)}>
                <DeleteOutlineOutlined fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        ),
      },
    ],
    [allFields, onDelete, onEdit]
  );

  const data = useMemo(
    () =>
      fields.map((field, index) => ({
        ...field,
        order_id: field.order_id ?? index + 1,
        _hasError: Boolean(fieldErrors?.[index]),
      })),
    [fieldErrors, fields]
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      getRowId={(row) => String(row.item_id ?? row.key_name)}
      enableSorting={false}
      enableRowOrdering={enableReorder}
      muiRowDragHandleProps={
        enableReorder
          ? ({ table }) => ({
              onDragEnd: () => {
                const { draggingRow, hoveredRow } = table.getState();
                if (!draggingRow || !hoveredRow) return;
                const reordered = [...fields];
                reordered.splice(
                  hoveredRow.index,
                  0,
                  reordered.splice(draggingRow.index, 1)[0]
                );
                onReorder?.(
                  reordered.map((field, index) => ({
                    ...field,
                    order_id: index + 1,
                  }))
                );
              },
            })
          : undefined
      }
      aria-label="Form fields table"
    />
  );
}
